/**
 * /pos — CATÁLOGO · POS-TODAS
 *
 * Página de catálogo das 6 pós-graduações da FEO. Recebe tráfego pago e
 * orgânico e distribui para as LPs de venda de cada pós (hoje Wix, versão A).
 * Substitui a antiga LP RD Station em materiais.fisioemortopedia.com.br/pos.
 *
 * Design: dark, minimal, profissional. Grid 3x2 (desktop) / 2x3 (tablet) /
 * 1 coluna (mobile) com as thumbs oficiais 4:5 (título já embutido na arte).
 */
import { useTeamNumbers } from "@/lib/team-numbers";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { redirecionaCatalogoBlackfeo } from "@/lib/black-november/ofertas-lps";
import { BlackTopBar } from "@/components/black2x1/BlackTopBar";


import { getAcoesStateFn } from "@/lib/campanhas/acoes.functions";
import { useState, type ReactNode } from "react";
import { ArrowRight, GraduationCap, Award, Users, Sparkles, MessageCircle, CheckCircle2, AlertTriangle, Search } from "lucide-react";

import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { useSiteLink } from "@/lib/managed-links/use-site-link";

import { POS_INFO, ALL_PRODUCTS } from "@/lib/checkout/links";
import { POS_THEMES } from "@/components/posfigma/theme";
import { PROFESSORES_BY_PRODUCT } from "@/components/posfigma/professores-by-product";
import type { ProductSlug } from "@/components/checkout/PreCheckoutDialog";

import logoFeo from "@/assets/feo/logo-feo.png";
import thumbOrto from "@/assets/pos/thumb-ortopedia.webp";
import thumbDor from "@/assets/pos/thumb-dor.webp";
import thumbEsporte from "@/assets/pos/thumb-esporte.webp";
import thumbGeronto from "@/assets/pos/thumb-geronto.webp";
import thumbTrauma from "@/assets/pos/thumb-traumato.webp";
import { BlackDestaqueSection } from "@/components/black2x1/BlackDestaqueSection";
import thumbPrescricao from "@/assets/pos/thumb-prescricao.webp";

const THUMBS: Record<ProductSlug, string> = {
  orto: thumbOrto,
  dor: thumbDor,
  esporte: thumbEsporte,
  geronto: thumbGeronto,
  trauma: thumbTrauma,
  prescricao: thumbPrescricao,
};

/**
 * Descrição oficial em 3 linhas, extraída da 1ª seção da LP de cada pós.
 * Não inventar — se mudar na LP de origem, atualizar aqui.
 */
const SUBTITLE: Record<ProductSlug, ReactNode> = {
  orto: (
    <>
      Fisioterapia musculoesquelética,
      <br /> para que você avalie e atenda
      <br /> as principais condições clínicas da área.
    </>
  ),
  dor: (
    <>
      Manejo da dor,
      <br /> para que você avalie e cuide
      <br /> de quadros agudos ou crônicos.
    </>
  ),
  esporte: (
    <>
      Fisioterapia no esporte,
      <br /> do recreacional ao alto rendimento,
      <br /> com foco em recuperação e desempenho.
    </>
  ),
  geronto: (
    <>
      Pessoa idosa,
      <br /> para acompanhar do diagnóstico ao tratamento
      <br /> as condições de saúde e funcionalidade.
    </>
  ),
  trauma: (
    <>
      Primeira pós online do Brasil
      <br /> em traumatologia ortopédica,
      <br /> exclusiva para fisioterapeutas.
    </>
  ),
  prescricao: (
    <>
      Prescrição de exercícios,
      <br /> com raciocínio clínico baseado em evidências
      <br /> e cuidado centrado na pessoa.
    </>
  ),
};

/** Desconto do lote promocional por pós-graduação. */
const DESCONTO: Record<ProductSlug, string> = {
  orto: "R$ 3.000 OFF",
  dor: "R$ 3.000 OFF",
  esporte: "R$ 3.000 OFF",
  geronto: "R$ 3.000 OFF",
  trauma: "R$ 1.100 OFF",
  prescricao: "R$ 1.100 OFF",
};

/** Preview do corpo docente para a seção institucional. */
const DOCENTE_PREVIEW: string[] = (() => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const product of ALL_PRODUCTS) {
    for (const p of PROFESSORES_BY_PRODUCT[product]) {
      if (seen.has(p.nome)) continue;
      seen.add(p.nome);
      out.push(p.foto);
      if (out.length >= 9) return out;
    }
  }
  return out;
})();

/** Link direto para o CRM (mesmo usado na LP RD antiga). */
const CRM_LINK_DEFAULT = "https://nimble.li/bd362gxd";

export const Route = createFileRoute("/pos-gg")({
  head: () => ({
    meta: [
      { title: "Catálogo Pós FEO · Campanha Google" },
      { name: "robots", content: "noindex,nofollow" },

      {
        name: "description",
        content:
          "Variante da campanha Google Ads do catálogo de pós-graduações FEO. Uso interno para atribuição de tráfego pago, conteúdo idêntico ao catálogo principal.",
      },
      { property: "og:title", content: "Catálogo Pós FEO · Campanha Google" },
      {
        property: "og:description",
        content:
          "Variante interna do catálogo de pós-graduações usada nas campanhas do Google Ads.",
      },
      { property: "og:url", content: "https://fisioemortopedia.com.br/pos-gg" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Catálogo Pós FEO · Campanha Google" },
      {
        name: "twitter:description",
        content: "Variante interna do catálogo de pós-graduações (Google Ads).",
      },
    ],
    links: [{ rel: "canonical", href: "https://fisioemortopedia.com.br/pos-gg" }],
  }),
  /**
   * Durante a Semana da Fisioterapia o catálogo Google redireciona para a
   * LP própria da ação (/semanadafisiogg), que aponta as 6 pós para as LPs
   * Wix /gg. O redirect precisa acontecer aqui (nunca no componente).
   */
  beforeLoad: async ({ location }) => {
    const destino = redirecionaCatalogoBlackfeo(location.searchStr, Date.now());
    if (destino) throw redirect({ href: destino });
    const acoes = await getAcoesStateFn();
    if (acoes?.semanadafisio?.ativa) {
      throw redirect({ to: "/semanadafisiogg" });
    }
  },
  component: CatalogoPosGgRoute,
});

function CatalogoPosGgRoute() {
  return <CatalogoPosPage />;
}

const UTM = "";

const GG_LINKS: Record<ProductSlug, string> = {
  orto: "https://pos-orto.fisioemortopedia.com.br/gg",
  dor: "https://pos-dor.fisioemortopedia.com.br/gg",
  esporte: "https://pos-esporte.fisioemortopedia.com.br/gg",
  geronto: "https://pos-geronto.fisioemortopedia.com.br/gg",
  trauma: "https://pos-traumato.fisioemortopedia.com.br/gg",
  prescricao: "https://pos-prescricao.fisioemortopedia.com.br/gg",
};

function CatalogoPosPage() {
  const CRM_LINK = useSiteLink("link.cta.wa-atendimento-pos", CRM_LINK_DEFAULT);
  return (

    <div className="min-h-screen bg-[#0A0F2C] text-white">
      <BlackTopBar origem="pos-gg" tall />

      {/* Hero — LOTE PROMOCIONAL em destaque */}
      <section className="container mx-auto max-w-4xl px-4 sm:px-6 pt-8 pb-8 sm:pt-12 sm:pb-12 text-center">
        <img decoding="async" src={logoFeo} alt="Fisio em Ortopedia" className="mx-auto mb-6 sm:mb-8 h-10 sm:h-12 w-auto" />
        <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[#F5C518]/40 bg-[#F5C518]/10 px-2.5 sm:px-4 py-1 sm:py-1.5 text-[0.65rem] sm:text-xs font-bold tracking-wider text-[#F5C518] uppercase">
          Lote Promocional · Vagas Limitadas
        </span>
        <h1 className="mt-6 text-[clamp(1.25rem,6.5vw,1.625rem)] sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.15]">
          Todas as nossas{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00C9A7] to-[#7C7FFF]">
            pós-<br className="block sm:hidden" />graduações
          </span>{" "}
          com desconto <br className="block sm:hidden" /> de lote promocional
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-[0.8125rem] sm:text-lg text-white/70 leading-snug sm:leading-relaxed">
          Não perca essa chance de mudar a sua carreira. Escolha a sua área e garanta a sua vaga com a condição especial. É por tempo limitado!
        </p>

        <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap sm:justify-center items-center gap-3 sm:gap-6 text-sm text-white/70">
          <span className="inline-flex items-center gap-2"><GraduationCap className="h-4 w-4 text-[#00C9A7]" /> +80 professores</span>
          <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-[#00C9A7]" /> +15 mil alunos</span>
          <span className="inline-flex items-center gap-2"><Award className="h-4 w-4 text-[#00C9A7]" /> Reconhecido pelo MEC</span>
        </div>
      </section>

      {/* Grid de pós — CTA na cor de cada pós */}
      <section className="container mx-auto max-w-5xl px-8 sm:px-6 pb-16 sm:pb-24">
        <div className="grid grid-cols-1 px-6 sm:px-0 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-6">
          {ALL_PRODUCTS.map((slug) => {
            const info = POS_INFO[slug];
            const theme = POS_THEMES[slug];
            return (
              <a
                key={slug}
                href={GG_LINKS[slug]}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/5 transition-all hover:-translate-y-1"
                style={{
                  boxShadow: `0 0 0 1px transparent`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 20px 40px -20px ${theme.primary}55`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "";
                  e.currentTarget.style.boxShadow = "";
                }}
              >
                <div className="aspect-[4/5] w-full overflow-hidden bg-black/20">
                  <img
                    src={THUMBS[slug]}
                    alt={info.nome}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-3 p-4 sm:gap-4 sm:p-6 text-center">
                  <span
                    className="inline-flex items-center justify-center gap-2 rounded-full px-3 py-1.5 sm:py-1 text-xs font-extrabold tracking-wide"
                    style={{
                      color: theme.primary,
                      backgroundColor: `${theme.primary}15`,
                      border: `1px solid ${theme.primary}40`,
                    }}
                  >
                    {DESCONTO[slug]}
                  </span>
                  <span
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 sm:py-3 text-sm font-bold text-white transition-transform group-hover:scale-[1.02]"
                    style={{
                      background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
                    }}
                  >
                    Aproveitar lote promocional
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>

              </a>
            );
          })}
        </div>
      </section>

      <BlackDestaqueSection origem="pos-gg" />

      {/* Corpo docente — referência sutil antes do CRM */}
      <DocentesSection />

      {/* Faixa CRM — WhatsApp + verificador de números oficiais */}
      <WhatsAppSection />

      <a
        href={CRM_LINK}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Fale com nosso time no WhatsApp"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center justify-center rounded-full bg-[#25D366] p-4 text-white shadow-lg shadow-[#25D366]/30 transition-transform hover:scale-110"
      >
        <MessageCircle className="h-6 w-6" />
      </a>

      <MinimalFooter />
    </div>
  );
}


function DocentesSection() {
  return (
    <section className="border-t border-white/10 bg-white/[0.02]">
      <div className="container mx-auto px-5 sm:px-6 py-14 sm:py-20">
        <div className="mx-auto max-w-5xl rounded-3xl border border-white/10 bg-white/5 p-6 sm:p-12 grid lg:grid-cols-[1.2fr_1fr] gap-6 sm:gap-8 items-center">
          <div className="text-center lg:text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-[#00C9A7]">
              Corpo docente
            </p>
            <h2 className="text-[1.35rem] sm:text-3xl lg:text-4xl font-extrabold leading-tight">
              Mais de 80 professores referência em{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00C9A7] to-[#7C7FFF]">
                fisioterapia musculoesquelética
              </span>
            </h2>
            <p className="mt-4 text-[0.8125rem] sm:text-base text-white/70 leading-snug sm:leading-relaxed">
              Especialistas, mestres e doutores que assinam nossas pós em Ortopedia, Dor, Esporte, Gerontologia e mais.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 justify-items-center">
            {DOCENTE_PREVIEW.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                loading="lazy"
                className="h-14 w-14 sm:h-20 sm:w-20 rounded-full object-cover border-2 border-white/10"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function formatBrPhone(raw: string) {
  const v = raw.replace(/\D/g, "").slice(0, 11);
  if (v.length > 6) return v.replace(/(\d{2})(\d{5})(\d+)/, "($1) $2-$3");
  if (v.length > 2) return v.replace(/(\d{2})(\d+)/, "($1) $2");
  return v;
}

function WhatsAppSection() {
  const TEAM_NUMBERS = useTeamNumbers();
  const CRM_LINK = useSiteLink("link.cta.wa-atendimento-pos", CRM_LINK_DEFAULT);
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "ok" | "fail" | "invalid">("idle");


  const verificar = () => {
    const digits = value.replace(/\D/g, "").slice(-11);
    if (digits.length < 10) {
      setStatus("invalid");
      return;
    }
    setStatus("loading");
    setTimeout(() => {
      const full = "55" + digits;
      setStatus(TEAM_NUMBERS.includes(full) ? "ok" : "fail");
    }, 600);
  };

  return (
    <section className="border-t border-white/10 bg-white/[0.02]">
      <div className="container mx-auto px-5 sm:px-6 py-14 sm:py-20 text-center">
        <h2 className="text-2xl sm:text-4xl font-black">Ficou alguma dúvida?</h2>
        <a
          href={CRM_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-5 sm:px-7 py-4 text-sm sm:text-lg font-bold text-white shadow-lg shadow-[#25D366]/20 transition-transform hover:scale-[1.03] whitespace-nowrap"
        >
          <MessageCircle className="h-5 w-5 shrink-0" />
          Fale com nosso time no WhatsApp!
        </a>

        {/* Verificador de números oficiais */}
        <div className="mx-auto mt-10 max-w-xl rounded-2xl border border-white/10 bg-white/5 p-5 sm:p-8 text-center sm:text-left shadow-2xl">
          <h3 className="text-center text-base sm:text-2xl font-bold text-white">
            Verifique o número do nosso time
          </h3>
          <p className="mt-2 text-center text-[0.8125rem] sm:text-sm text-white/60">
            Digite o número e confirme se <span className="block sm:hidden" /> pertence à nossa equipe
          </p>

          <div className="mt-5 flex flex-col sm:flex-row items-stretch gap-2 rounded-xl bg-white/5 p-2 shadow-inner border border-white/10">
            <div className="hidden sm:flex items-center pl-3 text-sm font-semibold text-white/50">
              BR
            </div>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="(00) 00000-0000"
              value={value}
              onChange={(e) => setValue(formatBrPhone(e.target.value))}
              onKeyDown={(e) => e.key === "Enter" && verificar()}
              className="flex-1 bg-transparent px-3 py-3 text-base text-white outline-none placeholder:text-white/40"
              maxLength={16}
            />
            <button
              type="button"
              onClick={verificar}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-bold text-[#0A0F2C] transition-colors hover:bg-white/90"
            >
              <Search className="h-4 w-4" />
              VERIFICAR
            </button>
          </div>

          {status === "loading" && (
            <div className="mt-4 rounded-lg bg-white/5 px-4 py-3 text-sm font-semibold text-white/70">
              🔎 Verificando número...
            </div>
          )}
          {status === "invalid" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              <AlertTriangle className="h-4 w-4" /> Digite um número válido com DDD
            </div>
          )}
          {status === "ok" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#00C9A7]/10 px-4 py-3 text-sm font-semibold text-[#00C9A7]">
              <CheckCircle2 className="h-4 w-4" />
              Este número faz parte do time da FEO
              <span className="ml-auto rounded-full bg-[#00C9A7] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#0A0F2C]">
                Verificado
              </span>
            </div>
          )}
          {status === "fail" && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-400">
              <AlertTriangle className="h-4 w-4" /> Este número NÃO pertence ao nosso time
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


